import { useAuth } from '../../components/auth/AuthProvider'
import { Navigate, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { API_BASE_URL } from '../../lib/config'
import { UnauthorizedAccessModal } from '../../components/UnauthorizedAccessModal'
import { LoadingSpinner } from '../../components/shared/LoadingSpinner'

interface AdminProtectedRouteProps {
  children: React.ReactNode
  redirectTo?: string
}

interface AdminStatus {
  is_admin: boolean
  user_email: string | null
  user_role: string | null
  user_info: {
    id: string
    username: string
    first_name: string | null
    last_name: string | null
  } | null
}

export function AdminProtectedRoute({ children, redirectTo = '/' }: AdminProtectedRouteProps) {
  const { isSignedIn, loading, user, credentialUser, dbUser } = useAuth()
  const navigate = useNavigate()
  const [adminStatus, setAdminStatus] = useState<AdminStatus | null>(null)
  const [checkingAdmin, setCheckingAdmin] = useState(false)
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false)
  const [hasCheckedAdmin, setHasCheckedAdmin] = useState(false)

  // Check admin status from database
  useEffect(() => {
    const checkAdminStatus = async () => {
      console.log('AdminProtectedRoute: Checking admin status...', { isSignedIn, user: user?.email })
      
      const emailForAdmin = user?.email || dbUser?.email || ''
      if (!isSignedIn || !emailForAdmin) {
        console.log('AdminProtectedRoute: Not signed in or no user, returning')
        setHasCheckedAdmin(true)
        return
      }

      setCheckingAdmin(true)
      try {
        console.log('AdminProtectedRoute: Making API call to check admin status')
        const response = await fetch(`${API_BASE_URL}admin/check-status`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'x-user-email': emailForAdmin
          }
        })

        console.log('AdminProtectedRoute: API response status:', response.status)
        
        if (response.ok) {
          const status = await response.json()
          console.log('AdminProtectedRoute: Admin status result:', status)
          setAdminStatus(status)
        } else {
          console.log('AdminProtectedRoute: API call failed with status:', response.status)
          setAdminStatus({ is_admin: false, user_email: null, user_role: null, user_info: null })
        }
      } catch (error) {
        console.error('AdminProtectedRoute: Error checking admin status:', error)
        setAdminStatus({ is_admin: false, user_email: null, user_role: null, user_info: null })
      } finally {
        setCheckingAdmin(false)
        setHasCheckedAdmin(true)
      }
    }

    checkAdminStatus()
  }, [isSignedIn, user, dbUser])

  console.log('AdminProtectedRoute: Render state:', { 
    loading, 
    checkingAdmin, 
    hasCheckedAdmin,
    isSignedIn, 
    user: user?.email, 
    adminStatus, 
    isAdmin: adminStatus?.is_admin 
  })

  // Show loading while checking authentication or admin status
  if (loading || checkingAdmin || !hasCheckedAdmin) {
    console.log('AdminProtectedRoute: Showing loading spinner')
    return <LoadingSpinner fullScreen size="xl" variant="minimal" message="Checking admin access..." />
  }

  // Redirect if not signed in
  const resolvedEmail = user?.email || dbUser?.email || null
  if (!isSignedIn || !resolvedEmail) {
    console.log('AdminProtectedRoute: Not signed in, redirecting to:', redirectTo)
    return <Navigate to={redirectTo} replace />
  }

  // Check if user is admin from database
  const isAdmin = adminStatus?.is_admin === true
  console.log('AdminProtectedRoute: Final admin check:', { isAdmin, adminStatus })

  // If not admin, show the unauthorized modal and redirect
  if (!isAdmin) {
    console.log('AdminProtectedRoute: User is not admin, showing unauthorized modal and redirecting')
    return (
      <>
        <Navigate to={redirectTo} replace />
        <UnauthorizedAccessModal
          isOpen={showUnauthorizedModal}
          onClose={() => {
            setShowUnauthorizedModal(false)
            navigate(redirectTo)
          }}
          userEmail={resolvedEmail}
          userRole={adminStatus?.user_role || 'user'}
          onGoBack={() => {
            setShowUnauthorizedModal(false)
            navigate(redirectTo)
          }}
        />
      </>
    )
  }

  console.log('AdminProtectedRoute: User is admin, rendering children')
  return <>{children}</>
}

